import { Link } from 'react-router-dom';
import {
  ArrowIcon, PhoneLink, SERVICES, Seo, SiteFooter, SiteHeader, useScrollToHash,
} from '../components/shared';

const GALLERY = [
  { src: '/photos/before-after-backyard.jpg', w: 1000, h: 844, alt: 'Overgrown backyard before and after mowing', caption: 'Overgrown backyard, cut and trimmed' },
  { src: '/photos/before-after-garden-bed.jpg', w: 1000, h: 844, alt: 'Garden bed before and after weeding', caption: 'Front garden bed, weeded by hand' },
  { src: '/photos/before-after-walkway.jpg', w: 1000, h: 844, alt: 'Snow-filled walkway before and after clearing', caption: 'Walkway cleared after a heavy snowfall' },
  { src: '/photos/before-after-tall-grass.jpg', w: 1000, h: 1000, alt: 'Tall grass before and after a full cut', caption: 'Knee-high grass brought back under control' },
  { src: '/photos/before-after-side-path.jpg', w: 1000, h: 1000, alt: 'Overgrown side path before and after clearing', caption: 'Side path cleared back to the paving' },
  { src: '/photos/before-after-stone-path.jpg', w: 1000, h: 844, alt: 'Backyard with a stone path and cut lawn', caption: 'Back garden mowed around the stonework' },
];

const STEPS = [
  { title: 'Book a quote', text: 'Tell us your address and what needs doing. Photos help but are not required.' },
  { title: 'We price it', text: 'You get a quote for the whole job, not an hourly rate. Approve it and we schedule a student.' },
  { title: 'The work gets done', text: 'Before and after photos come to you. Once you approve, the student gets paid.' },
];

export default function HomeOwnersPage() {
  useScrollToHash();

  return (
    <div className="page">
      <Seo
        title="Lawn Care & Yard Services by Local Students | Student For Hire"
        description="Lawn mowing, weeding, leaf cleanups, snow removal and more, done by ID-checked local students. Book a free quote."
      />

      <SiteHeader>
        <a href="#services" className="nav-link nav-link--wide">Services</a>
        <a href="#seasonal-packages" className="nav-link nav-link--wide">Our work</a>
        <Link to="/students" className="nav-link">For students</Link>
        <PhoneLink />
        <Link to="/book" className="btn btn--primary btn--header">Get a quote</Link>
      </SiteHeader>

      <main>
        <section className="hero hero--owners">
          <img className="hero__img" src="/photos/front-lawn-mowing.jpg" alt="A freshly cut front lawn" width="1600" height="1200" loading="eager" />
          <div className="hero__scrim" />
          <div className="hero__inner">
            <div className="wrap">
              <div className="hero__copy">
                <div className="eyebrow">For home owners</div>
                <h1 className="h1">Hand the yard to a student.</h1>
                <p className="hero__lede">
                  Mowing, weeding, cleanups and snow removal, done by vetted students from your own neighbourhood. Tell us what you
                  need and we send you a quote.
                </p>
                <div className="hero__actions">
                  <Link to="/book" className="btn btn--primary btn--lg">Get a free quote <ArrowIcon /></Link>
                  <a href="#seasonal-packages" className="btn btn--ghost">See our work</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="stat-bar" aria-label="Why Student For Hire">
          <div className="wrap">
            <div className="stat">
              <div className="stat__title">Free quotes</div>
              <div className="stat__text">Book a quote and we price the job for you, no obligation.</div>
            </div>
            <div className="stat">
              <div className="stat__title">ID-checked students</div>
              <div className="stat__text">Photo ID, a selfie and a student card before anyone works.</div>
            </div>
            <div className="stat">
              <div className="stat__title">You approve the work</div>
              <div className="stat__text">Before and after photos on every job, approved by you.</div>
            </div>
          </div>
        </section>

        <section id="services" className="section">
          <div className="wrap">
            <h2 className="h2">What we take care of.</h2>
            <p className="lede">
              Every job is quoted individually — book a quote and we price it around your property, not a stopwatch.
            </p>
            <ul className="service-grid">
              {SERVICES.map(({ key, label, Icon }) => (
                <li key={key} className="service-tile">
                  <Icon size={42} />
                  <span className="service-tile__label">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="seasonal-packages" className="section section--white">
          <div className="wrap">
            <h2 className="h2">Real jobs, before and after.</h2>
            <p className="lede gallery-lede">Every one of these was done by a student on our roster, photographed on the day.</p>
            <div className="gallery">
              {GALLERY.map((g) => (
                <figure key={g.src}>
                  <img src={g.src} alt={g.alt} width={g.w} height={g.h} loading="lazy" />
                  <figcaption>{g.caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="wrap">
            <h2 className="h2 steps-title">Booking takes three steps.</h2>
            <div className="hairline-grid">
              {STEPS.map((s, i) => (
                <div key={s.title} className="hairline-grid__cell">
                  <div className="step-num">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="h3">{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="referrals" className="section section--navy section--center">
          <div className="wrap">
            <h2 className="h2">Book a free quote.</h2>
            <p>Send us the job and we will come back with a price. No hourly rates, no subscription, no obligation.</p>
            <Link to="/book" className="btn btn--primary btn--lg btn--on-navy">Get a free quote <ArrowIcon /></Link>
          </div>
        </section>
      </main>

      <SiteFooter crossLink={{ to: '/students', label: 'For students' }} />
    </div>
  );
}
